

import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { IconButton, Modal, Box, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { axiosInstance } from "../../../utility/apiInstance";
import { SubscriptionData, SubscriptionResponse } from "./types/SubscriptionTypes";
import toast from "react-hot-toast";

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 900,
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 10,
  p: 3,
};

function TabSubscriptions({ item }: { item: any }) {
  const [alertsToBeTriggered, setAlertsToBeTriggered] = useState<SubscriptionData[]>([]);
  const [loading, setLoading] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<SubscriptionData | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [sendedAlertData, setSendedAlertData] = useState<any>([]);

  const fetchAlertsToBeTriggered = async () => {
    try {
      let url = null;
      if (item["for"] === "air") {
        url = `/api/v1/subscription/subscription/${item.mawb}`;
      } else {
        url = `/api/v1/ocean/subscription/subscriptions/${item.mawb}`;
      }

      const { data } = await axiosInstance.get<SubscriptionResponse>(url);
      setAlertsToBeTriggered(data?.data || []);
    } catch (error) {
      setAlertsToBeTriggered([]);
    }
  };

  const handleRefreshClick = async (selectedItem: SubscriptionData) => {
    try {
      setLoading(true);
      let url = "";

      if (item["for"] === "air") {
        url = `/api/v1/alert/awb`;
      } else {
        url = `/api/v1/alert/ocean`;
      }

      const payload = {
        trackingNumber: selectedItem.trackingNumber,
        milestone: selectedItem.milestones.milestone,
        alertType: selectedItem.milestones.channels,
        contacts: selectedItem.milestones.recipients,
      };

      const { data } = await axiosInstance.post(url, payload);
      toast.success(data?.message || "Alert triggered successfully");
      fetchAlertsToBeTriggered();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to trigger alert");
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = async (alert: SubscriptionData) => {
    setSelectedAlert(alert);
    setOpenModal(true);
    setModalLoading(true);
    setSendedAlertData(null);

    try {
      const filter = {
        trackingNumber: alert.trackingNumber,
        milestone: alert.milestones.milestone,
        alertType: alert.milestones.channels,
      };
      let url = "/api/v1/alert/get-alert-send";
      const { data } = await axiosInstance.post(url, filter);
      setSendedAlertData(data?.data || []);
    } catch (error) {
      toast.error("Failed to fetch subscription details");
    } finally {
      setModalLoading(false);
    }
  };

  useEffect(() => {
    fetchAlertsToBeTriggered();
  }, []);

  return (
    <div className="pt-3">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-[3px] h-4 bg-[#F8B71F] rounded-full" />
          <span className="text-[12px] font-semibold text-gray-700 uppercase tracking-[0.06em]">
            Subscriptions
          </span>
        </div>
        <span className="text-[11px] text-gray-400 font-medium">
          {alertsToBeTriggered.length} alert{alertsToBeTriggered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Table Header (card-grid style) */}
      <div className="overflow-x-auto">
        <div className="grid grid-cols-[1.2fr_2fr_1fr_120px] min-w-[760px] px-3 mb-1.5">
          <span className="px-1 text-[10.5px] font-semibold text-gray-400 uppercase tracking-[0.07em]">Milestone</span>
          <span className="px-1 text-[10.5px] font-semibold text-gray-400 uppercase tracking-[0.07em]">Recipients</span>
          <span className="px-1 text-[10.5px] font-semibold text-gray-400 uppercase tracking-[0.07em]">Channel</span>
          <span className="px-1 text-[10.5px] font-semibold text-gray-400 uppercase tracking-[0.07em]">Actions</span>
        </div>

        {/* Rows */}
        {alertsToBeTriggered.length > 0 ? (
          alertsToBeTriggered.map((alert: SubscriptionData, index) => {
            const achieved = !!alert?.milestones?.isAchieved;

            return (
              <div
                key={index}
                className={`grid grid-cols-[1.2fr_2fr_1fr_120px] items-center min-w-[760px] rounded-xl border px-3 py-2 mb-1.5 transition
                  ${
                    achieved
                      ? "bg-green-50 border-green-200"
                      : "bg-white border-gray-200"
                  } hover:shadow-sm`}
              >
                <div className="px-1">
                  <span className={`text-[12.5px] font-semibold ${achieved ? "text-green-700" : "text-gray-700"}`}>
                    {alert.milestones.milestone}
                  </span>
                </div>

                <div className="px-1 text-[12px] text-gray-700">
                  {alert.milestones.recipients
                    .map((r: any) => `${r.salutation} ${r.name} (${alert.milestones.channels=="email"?r.email:r.whatappNumber},${r?.contactOrganisation||""})`)
                    .join(", ")}
                </div>

                <div className="px-1">
                  <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 text-[11px] font-semibold px-2 py-1 capitalize">
                    {alert.milestones.channels}
                  </span>
                </div>

                <div className="px-1 flex items-center gap-1 justify-start">
                  <IconButton
                    sx={{ color: "#374151" }}
                    disabled={!achieved || loading}
                    onClick={() => handleViewClick(alert)}
                    size="small"
                  >
                    <RemoveRedEyeOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    sx={{ color: "#374151" }}
                    onClick={() => handleRefreshClick(alert)}
                    disabled={!achieved || loading}
                    size="small"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            );
          })
        ) : (
          <div className="min-h-[110px] rounded-xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-400 text-sm">
            No subscriptions found.
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="view-subscription-modal"
        aria-describedby="view-subscription-details"
      >
        <Box sx={modalStyle}>
          {modalLoading ? (
            <div className="flex justify-center items-center h-40">
              <CircularProgress />
            </div>
          ) : sendedAlertData ? (
            <div className="space-y-4 text-sm text-gray-700">
              <h2 className="text-lg font-semibold border-b border-gray-200 pb-2">
                Subscription Details
              </h2>

              <div className="grid sm:grid-cols-2 gap-2 text-[13px]">
                <p><span className="font-medium">Tracking:</span> {selectedAlert?.trackingNumber}</p>
                <p><span className="font-medium">Milestone:</span> {selectedAlert?.milestones?.milestone}</p>
                <p className="sm:col-span-2">
                  <span className="font-medium">Recipients:</span>{" "}
                  {selectedAlert?.milestones?.recipients
                    .map((r: any) => `${r.salutation} ${r.name}`)
                    .join(", ")}
                </p>
                <p><span className="font-medium">Channel:</span> {selectedAlert?.milestones?.channels}</p>
              </div>

              {sendedAlertData.length > 0 ? (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="table-auto w-full text-xs">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Type</th>
                        <th className="p-2 text-left">Milestone</th>
                        <th className="p-2 text-left">Message</th>
                        <th className="p-2 text-left">Origin</th>
                        <th className="p-2 text-left">Dest</th>
                        <th className="p-2 text-left">Airline</th>
                        <th className="p-2 text-left">Contact</th>
                        <th className="p-2 text-left">Event</th>
                        <th className="p-2 text-left">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sendedAlertData.map((alert: any, i: number) => (
                        <tr key={alert._id || i} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="p-2">{alert.alertType}</td>
                          <td className="p-2">{alert.milestone}</td>
                          <td className="p-2">{alert.messageSent}</td>
                          <td className="p-2">{alert.origin}</td>
                          <td className="p-2">{alert.destination}</td>
                          <td className="p-2">{alert.airlineName}</td>
                          <td className="p-2">{alert.contact}</td>
                          <td className="p-2">
                            {alert.eventDate ? new Date(alert.eventDate).toLocaleDateString() : "-"}
                          </td>
                          <td className="p-2">
                            {alert.createdAt ? new Date(alert.createdAt).toLocaleString() : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-gray-400 text-sm mt-2">
                  No alert history found
                </p>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-400">No data available</p>
          )}
        </Box>
      </Modal>
    </div>
  );
}

export default TabSubscriptions;